import { FormProvider } from 'react-hook-form'
import { type FC, Fragment } from 'react'

import type { DialogProps } from '@wartek-id/dialog-v1'

import Dialog from 'app/KinerjaKSPage/components/CardDataConfirmation/Dialog'
import useSchoolContext from 'app/KinerjaKSPage/hooks/useSchoolContext'
import { useConfirmationData } from 'app/KinerjaKSPage/components/CardDataConfirmation/utils/hooks'
import { Label } from 'app/KinerjaKSPage/components/CardDataConfirmation/Label'

import type { TAtasan } from 'types/kinerja-atasan'

type Props = Omit<DialogProps, 'children'> & {
  data: TAtasan
  isReconfirm: boolean
}

export const DialogConfirmation: FC<Props> = ({
  isOpen,
  onClose,
  data,
  isReconfirm = false,
  ...props
}) => {
  const { currentNpsn } = useSchoolContext()
  const {
    showContent,
    setShowContent,

    isConsentAccepted,
    setIsConsentAccepted,

    isConsentDinasAccepted,
    setIsConsentDinasAccepted,

    isConcentReconfirmAccepted,
    setIsConsentReconfirmAccepted,

    methods,

    isInputValid,
    dataPengawas,
    dataDinas,
    isProceedWithDinas,

    isSubmitting,
    submit,
    close,
  } = useConfirmationData({
    npsn: currentNpsn,
    data,
    isReconfirm,
    onClose: () => {
      onClose?.()
    },
  })

  return (
    <Dialog.Root isOpen={isOpen} {...props}>
      <div className="p-6">
        <FormProvider {...methods}>
          {/* Dialog: Konfirmasi ulang data atasan Anda? */}
          {showContent === 'CONSENT_RECONFIRM' && (
            <Dialog.ConsentReconfirm>
              <Dialog.ConsentForm
                text="Saya setuju untuk konfirmasi ulang data atasan."
                onChange={(isValid) => setIsConsentReconfirmAccepted(isValid)}
              />
              <Dialog.Buttons
                continueLabel="Lanjut"
                continueDisabled={!isConcentReconfirmAccepted}
                continueOnClick={() => setShowContent('FORM')}
                cancelOnClick={() => close?.()}
              />
            </Dialog.ConsentReconfirm>
          )}

          {/* Dialog: Konfirmasi data atasan Anda */}
          {showContent === 'FORM' && (
            <Dialog.Form {...props}>
              {['pengawas', 'dinas']?.map((item) => {
                const hasData = !!data[item]
                return (
                  <Dialog.FormField
                    key={item}
                    prefix={item}
                    hasData={hasData}
                    title={
                      item === 'pengawas'
                        ? 'Data Tim Kerja (Pengawas sekolah)'
                        : 'Data Daerah Dinas Pendidikan'
                    }
                    value={
                      <Label
                        status="UNCONFIRMED"
                        value={
                          item === 'pengawas'
                            ? data[item]?.nama
                            : data[item]?.wilayah
                        }
                        variant="action"
                      />
                    }
                  />
                )
              })}
              <Dialog.Buttons
                continueDisabled={!isInputValid}
                continueOnClick={() => setShowContent('CONSENT')}
                cancelOnClick={() => {
                  // Check the previous content, if it is CONCENT_RECONFIRM show CONSNET_RECONFIRM content, else close the dialog
                  if (isReconfirm) setShowContent('CONSENT_RECONFIRM')
                  else close?.()
                }}
              />
            </Dialog.Form>
          )}

          {/* Dialog: Lanjut dengan data ini? */}
          {showContent === 'CONSENT' && (
            <Dialog.Consent>
              <div className="flex flex-col gap-1">
                {Object.keys(methods.getValues())?.map((key) => (
                  <Fragment key={key}>
                    {key === 'pengawas' ? (
                      <Dialog.ConsentSummary
                        title="Data Tim Kerja"
                        label="Tim Kerja (Pengawas Sekolah)"
                        labelValue={
                          <Label
                            status="UNCONFIRMED"
                            value={dataPengawas?.label}
                          />
                        }
                        value={dataPengawas?.value}
                      />
                    ) : (
                      <Dialog.ConsentSummary
                        title="Data Daerah Dinas Pendidikan"
                        label="Daerah Dinas Pendidikan"
                        labelValue={
                          <Label
                            status="UNCONFIRMED"
                            value={dataDinas?.label}
                          />
                        }
                        value={dataDinas?.value}
                      />
                    )}
                  </Fragment>
                ))}
              </div>
              <Dialog.ConsentForm
                onChange={(isValid) => setIsConsentAccepted(isValid)}
              />
              <Dialog.Buttons
                continueLabel="Konfirmasi"
                continueDisabled={isSubmitting || !isConsentAccepted}
                continueOnClick={() => {
                  // Show dinas consent page if data ps is incorrect and dinas is correct
                  if (isProceedWithDinas) setShowContent('CONSENT_DINAS')
                  // Else send data to API
                  else submit()
                }}
                cancelDisabled={isSubmitting}
                cancelOnClick={() => {
                  setShowContent('FORM')
                }}
              />
            </Dialog.Consent>
          )}

          {/* Dialog: Lanjut dinilai oleh Dinas Pendidikan? */}
          {showContent === 'CONSENT_DINAS' && (
            <Dialog.ConsentDinas
              value={dataDinas?.label}
              isDisabled={isSubmitting}
              onConsentChange={(isAccepted) => {
                setIsConsentDinasAccepted(isAccepted)
              }}
            >
              <Dialog.Buttons
                continueLabel="Lanjut"
                continueDisabled={isSubmitting || !isConsentDinasAccepted}
                continueOnClick={() => {
                  // Send data to API
                  submit()
                }}
                cancelDisabled={isSubmitting}
                cancelOnClick={() => {
                  setShowContent('CONSENT')
                }}
              />
            </Dialog.ConsentDinas>
          )}
        </FormProvider>
      </div>
    </Dialog.Root>
  )
}
